import React, { useRef } from "react";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

const utahData = "/img/headerlogo.png";
const kpi = "/img/KPI-DASH 2.png";
const bodex = "/img/bodexLogo.png";
const pdf = "/img/pdf1.png";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function MiningTable21() {
  const pdfRef = useRef();

  const data = [
    {
      race_label: "American Indian or Alaska Native Alone",
      2018: 36,
      2019: 12,
      2020: 35,
      2021: 50,
      2022: 56,
      2023: 64,
      2024: 80,
    },
    {
      race_label: "Asian Alone",
      2018: 23,
      2019: 28,
      2020: 37,
      2021: 39,
      2022: 38,
      2023: 53,
      2024: 59,
    },
    {
      race_label: "Black or African American Alone",
      2018: 16,
      2019: 20,
      2020: 28,
      2021: 29,
      2022: 39,
      2023: 52,
      2024: 60,
    },
    {
      race_label: "Native Hawaiian or Other Pacific Islander Alone",
      2018: 23,
      2019: 25,
      2020: 30,
      2021: 27,
      2022: 32,
      2023: 39,
      2024: 44,
    },
    {
      race_label: "Two or More Race Groups",
      2018: 17,
      2019: 19,
      2020: 28,
      2021: 30,
      2022: 33,
      2023: 38,
      2024: 42,
    },
    {
      race_label: "White Alone",
      2018: 1316,
      2019: 1252,
      2020: 1720,
      2021: 1726,
      2022: 1892,
      2023: 2036,
      2024: 2172,
    },
  ];

  const years = ["2018", "2019", "2020", "2021", "2022", "2023", "2024"];
  const colors = [
    "#B45309", // blue
    "#10B981", // pink
    "#374151", // green
    "#10B981", // yellow
    "#DFCCF1", // purple
    "#9CA3AF", // red
  ];

  // Chart Data
  const chartData = {
    labels: years,
    datasets: data.map((d, i) => ({
      label: d.race_label,
      data: years.map((y) => d[y]),
      borderColor: colors[i % colors.length],
      backgroundColor: colors[i % colors.length],
      borderWidth: 2.5,
      tension: 0.35,
      pointRadius: 4,
    })),
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { position: "bottom" },
      title: {
        display: true,
        text: "🏳️ Employment Trend by Race (2018–2024)",
        font: { size: 16, weight: "bold" },
      },
    },
    scales: {
      x: {
        title: { display: true, text: "Year" },
        grid: { display: false },
      },
      y: {
        beginAtZero: true,
        title: { display: true, text: "Employment Count" },
      },
    },
  };

  // Summary Insights
  const growthData = data.map((d) => {
    const growth = ((d[2024] - d[2018]) / d[2018]) * 100;
    return { race: d.race_label, growth: growth.toFixed(1), latest: d[2024] };
  });

  const total2018 = data.reduce((sum, d) => sum + d[2018], 0);
  const total2024 = data.reduce((sum, d) => sum + d[2024], 0);
  const overallGrowth = (((total2024 - total2018) / total2018) * 100).toFixed(
    1
  );

  const topGrowth = growthData.reduce(
    (max, g) => (parseFloat(g.growth) > parseFloat(max.growth) ? g : max),
    growthData[0]
  );

  const topRace2024 = growthData.reduce(
    (max, g) => (g.latest > max.latest ? g : max),
    growthData[0]
  );

  // PDF Download
  const downloadPDF = () => {
    const input = pdfRef.current;
    html2canvas(input, {
      scale: 2,
      useCORS: true,
      backgroundColor: "#ffffff",
      scrollY: -window.scrollY,
    }).then((canvas) => {
      const imgData = canvas.toDataURL("image/png");
      const pdf = new jsPDF("p", "mm", "a4");

      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const imgWidth = pageWidth - 10;
      const imgHeight = (canvas.height * imgWidth) / canvas.width;

      let heightLeft = imgHeight;
      let position = 0;
      let pageNumber = 1;

      const addHeader = () => {
        pdf.addImage(utahData, "PNG", 60, 10, 32, 16);
        pdf.setFontSize(26);
        pdf.text("UTahData.org", 95, 22);
        pdf.link(50, 15, 60, 10, { url: "https://utahdata.org" });
        pdf.line(10, 35, 200, 35);
      };

      const addFooter = (pageNumber) => {
        pdf.setFontSize(9);
        const footerY = pageHeight - 10;
        pdf.line(10, footerY - 5, pageWidth - 10, footerY - 5);
        pdf.text(
          `Downloaded on: ${new Date().toLocaleDateString()}`,
          10,
          footerY
        );
        pdf.text(
          `Page ${pageNumber}`,
          pageWidth - 10,
          footerY,
          null,
          null,
          "right"
        );
      };

      addHeader();
      pdf.addImage(imgData, "PNG", 5, 32, imgWidth, imgHeight);
      heightLeft -= pageHeight - 32;

      while (heightLeft > 0) {
        pdf.addPage();
        pageNumber++;
        addHeader();
        position = heightLeft - imgHeight;
        pdf.addImage(imgData, "PNG", 5, position + 32, imgWidth, imgHeight);
        heightLeft -= pageHeight - 32;
        addFooter(pageNumber);
      }

      pdf.line(10, 260, 200, 260);
      const footerY = 265;
      pdf.addImage(utahData, "PNG", 22, footerY, 30, 15);
      pdf.setFontSize(10);
      pdf.text("Built by", 55, footerY + 10);
      pdf.addImage(bodex, "PNG", 68, footerY + 4, 44, 10);
      pdf.text("using", 112, footerY + 10);
      pdf.addImage(kpi, "PNG", 125, footerY, 20, 20);
      pdf.text("20250916.v3.198", 148, footerY + 10);
      pdf.text(`Downloaded on: ${new Date().toLocaleDateString()}`, 150, 295);
      pdf.text(`From: ${window.location.href}`, 10, 295);

      pdf.save("employment_trend_by_race_sector.pdf");
    });
  };

  return (
    <>
      <div
        ref={pdfRef}
        className="p-6 bg-white from-blue-50 via-white to-pink-50 rounded-xl shadow-md"
      >
        <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">
          Employment Trend by Race (2018–2024)
        </h2>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 text-center">
          <div className="p-4 bg-blue-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Overall Employment Growth
            </p>
            <p className="text-xl font-bold text-blue-800">+{overallGrowth}%</p>
          </div>
          <div className="p-4 bg-yellow-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Highest Growth Race
            </p>
            <p className="text-sm font-semibold text-yellow-800">
              {topGrowth.race}
            </p>
            <p className="text-lg font-bold text-yellow-800">
              +{topGrowth.growth}%
            </p>
          </div>
          <div className="p-4 bg-green-100 rounded-xl shadow">
            <p className="text-sm text-gray-600 font-medium">
              Top Employment Group (2024)
            </p>
            <p className="text-sm font-semibold text-green-800">
              {topRace2024.race}
            </p>
            <p className="text-lg font-bold text-green-800">
              {topRace2024.latest.toLocaleString("en-US")}
            </p>
          </div>
        </div>

        {/* Table + Chart */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          {/* Table */}
          {/* <div className="flex flex-col h-full"> */}
          <div className="overflow-x-auto flex items-center">
            <table className="w-full border-collapse text-center shadow-md rounded-lg overflow-hidden">
              <thead className="bg-gray-800 text-white">
                <tr>
                  <th className="p-2">Race</th>
                  {years.map((y) => (
                    <th key={y} className="p-2">
                      {y}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.map((row, i) => (
                  <tr
                    key={i}
                    className={`${
                      row.race_label === topRace2024.race
                        ? "bg-green-100 font-bold"
                        : "hover:bg-gray-100"
                    }`}
                  >
                    <td className="p-2 text-left">{row.race_label}</td>
                    {years.map((y) => (
                      <td key={y} className="p-2">
                        {row[y].toLocaleString()}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {/* </div> */}

          {/* Chart */}
          <div className="flex flex-col w-full overflow-x-auto bg-white rounded-lg shadow-md">
            <div className="min-w-[400px] w-full h-96 p-4">
              <Line data={chartData} options={options} />
            </div>
          </div>
        </div>
      </div>

      {/* Download PDF */}
      <div className="flex justify-center mt-4">
        <button
          onClick={downloadPDF}
          className="bg-red-700 hover:bg-red-900 px-4 py-2 text-white rounded-lg flex items-center gap-2 shadow-lg transition-transform hover:scale-105"
        >
          <span>Download as PDF</span>
          <img className="w-[1.5rem]" src={pdf} alt="pdf-icon" />
        </button>
      </div>
    </>
  );
}
